import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchVehicleCards,
  createVehicleCard,
  updateVehicleCard,
  deactivateVehicleCard,
} from "../../../../api/vehicles/cards";
import { CreditCardIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { ModuleAttachment } from "../../../utils/moduleAttachment/ModuleAttachment";
import { TagsView } from "../../../utils/moduleAttachment/TagsView";

// Create Card Modal Component
const CreateCardModal = ({ onClose, onSubmit, t, error }) => {
  const [cardType, setCardType] = useState("card");
  const [tags, setTags] = useState([]);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const form = e.target;
          const formData = new FormData(form);
          const cardType = formData.get("card_type");
          const cardNumber = formData.get("card_number")?.trim() || null;
          const issuer = formData.get("issuer")?.trim() || null;
          const serviceName = formData.get("service_name")?.trim() || null;
          
          const payload = {
            card_type: cardType,
            card_number: cardNumber,
            pin_code: formData.get("pin_code")?.trim() || null,
            issuer: issuer,
            service_name: serviceName,
            description: formData.get("description")?.trim() || null,
            active: true,
            module_tags: tags.map((tag) => ({
              module: tag.module,
              module_param: tag.module_param || tag.object?.id,
            })),
          };
          console.log("Submitting payload:", payload);
          onSubmit(payload);
        }}
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-lg font-semibold mb-4">{t("Add Card/Service")}</div>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {Array.isArray(error) ? error.join(", ") : typeof error === "object" ? JSON.stringify(error) : error}
                </h3>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Type")}</label>
            <select
              name="card_type"
              value={cardType}
              onChange={(e) => setCardType(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="card">{t("Card")}</option>
              <option value="service">{t("Service")}</option>
            </select>
          </div>
          {cardType === "card" ? (
            <>
              <input
                name="card_number"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Card Number")}
                required
              />
              <input
                name="pin_code"
                className="w-full border rounded px-3 py-2"
                placeholder={t("PIN Code (optional)")}
              />
              <input
                name="issuer"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Issuer (e.g., Circle K)")}
                required
              />
            </>
          ) : (
            <input
              name="service_name"
              className="w-full border rounded px-3 py-2"
              placeholder={t("Service Name (e.g., WashWorld)")}
              required
            />
          )}
          <textarea
            name="description"
            className="w-full border rounded px-3 py-2"
            placeholder={t("Description (optional)")}
            rows="3"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("Attach Quality Instructions/Routines")}
            </label>
            <ModuleAttachment
              tags={tags}
              onTagsChange={setTags}
              allowedModules={["hse_quality", "hse_instruction"]}
              showField={true}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded border">
            {t("Cancel")}
          </button>
          <button type="submit" className="px-3 py-2 rounded bg-blue-600 text-white">
            {t("Save")}
          </button>
        </div>
      </form>
    </div>
  );
};

// Edit Card Modal Component
const EditCardModal = ({ item, onClose, onSubmit, t, error }) => {
  const [cardType, setCardType] = useState(item?.card_type || "card");
  const [tags, setTags] = useState(item?.module_tags || []);
  
  // Update tags when item changes
  useEffect(() => {
    if (item?.module_tags) {
      setTags(item.module_tags);
    }
  }, [item]);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const form = e.target;
          const formData = new FormData(form);
          const cardType = formData.get("card_type");
          const cardNumber = formData.get("card_number")?.trim() || null;
          const issuer = formData.get("issuer")?.trim() || null;
          const serviceName = formData.get("service_name")?.trim() || null;
          
          const payload = {
            id: item.id,
            card_type: cardType,
            card_number: cardNumber,
            pin_code: formData.get("pin_code")?.trim() || null,
            issuer: issuer,
            service_name: serviceName,
            description: formData.get("description")?.trim() || null,
            active: formData.get("active") === "on",
            module_tags: tags.map((tag) => ({
              module: tag.module,
              module_param: tag.module_param || tag.object?.id,
            })),
          };
          console.log("Submitting update payload:", payload);
          onSubmit(payload);
        }}
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-lg font-semibold mb-4">{t("Edit Card/Service")}</div>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {Array.isArray(error) ? error.join(", ") : typeof error === "object" ? JSON.stringify(error) : error}
                </h3>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Type")}</label>
            <select
              name="card_type"
              value={cardType}
              onChange={(e) => setCardType(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="card">{t("Card")}</option>
              <option value="service">{t("Service")}</option>
            </select>
          </div>
          {cardType === "card" ? (
            <>
              <input
                name="card_number"
                defaultValue={item.card_number || ""}
                className="w-full border rounded px-3 py-2"
                placeholder={t("Card Number")}
              />
              <input
                name="pin_code"
                defaultValue={item.pin_code || ""}
                className="w-full border rounded px-3 py-2"
                placeholder={t("PIN Code")}
              />
              <input
                name="issuer"
                defaultValue={item.issuer || ""}
                className="w-full border rounded px-3 py-2"
                placeholder={t("Issuer")}
              />
            </>
          ) : (
            <input
              name="service_name"
              defaultValue={item.service_name || ""}
              className="w-full border rounded px-3 py-2"
              placeholder={t("Service Name")}
            />
          )}
          <textarea
            name="description"
            defaultValue={item.description || ""}
            className="w-full border rounded px-3 py-2"
            placeholder={t("Description")}
            rows="3"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("Attach Quality Instructions/Routines")}
            </label>
            <ModuleAttachment
              tags={tags}
              onTagsChange={setTags}
              allowedModules={["hse_quality", "hse_instruction"]}
              showField={true}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input name="active" type="checkbox" defaultChecked={item.active} /> {t("Active")}
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded border">
            {t("Cancel")}
          </button>
          <button type="submit" className="px-3 py-2 rounded bg-blue-600 text-white">
            {t("Save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export const VehicleCardsPanel = ({ vehicleId, canManage = false }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchVehicleCards({
          filters: [{ name: "vehicle_id", value: vehicleId }],
          per_page: 100,
          sortBy: "created_at",
          sort: "DESC",
        });
        // Backend returns { data: [...], totalCount: ..., currentCount: ..., page: ... }
        // axios already extracts r.data, so res is the JSON object
        const itemsData = res?.data || [];
        setItems(itemsData);
      } catch (e) {
        console.error("VehicleCardsPanel - Error loading vehicle cards:", e);
        setError("Failed to load vehicle cards");
      } finally {
        setIsLoading(false);
      }
    }
    if (vehicleId) load();
  }, [vehicleId]);

  const handleCreate = async (payload) => {
    try {
      setCreateError(null);
      const { module_tags, ...vehicleCardData } = payload;
      const requestPayload = {
        vehicle_card: { ...vehicleCardData, vehicle_id: vehicleId }
      };
      if (module_tags && module_tags.length > 0) {
        requestPayload.module_tags = module_tags;
      }
      const res = await createVehicleCard(requestPayload);
      if (res?.id) {
        setItems([res, ...items]);
        setShowCreate(false);
        setCreateError(null);
      } else if (res?.errors) {
        const errorMsg = Array.isArray(res.errors) 
          ? Object.values(res.errors).flat().join(", ")
          : typeof res.errors === "object"
          ? Object.entries(res.errors).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`).join("; ")
          : String(res.errors);
        setCreateError(errorMsg);
      } else {
        setCreateError("Failed to create vehicle card");
      }
    } catch (err) {
      console.error("Error creating vehicle card:", err);
      const errorMsg = err?.response?.data?.errors 
        ? (typeof err.response.data.errors === "object"
            ? Object.entries(err.response.data.errors).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`).join("; ")
            : String(err.response.data.errors))
        : err?.message || "Failed to create vehicle card";
      setCreateError(errorMsg);
    }
  };

  const handleUpdate = async (payload) => {
    try {
      setUpdateError(null);
      const { module_tags, id, ...vehicleCardData } = payload;
      const requestPayload = {
        vehicle_card: vehicleCardData
      };
      if (module_tags && module_tags.length > 0) {
        requestPayload.module_tags = module_tags;
      }
      const res = await updateVehicleCard({ 
        id,
        ...requestPayload
      });
      if (res?.id) {
        setItems(items.map((x) => (x.id === res.id ? res : x)));
        setEditItem(null);
        setUpdateError(null);
      } else if (res?.errors) {
        const errorMsg = Array.isArray(res.errors) 
          ? Object.values(res.errors).flat().join(", ")
          : typeof res.errors === "object"
          ? Object.entries(res.errors).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`).join("; ")
          : String(res.errors);
        setUpdateError(errorMsg);
      } else {
        setUpdateError("Failed to update vehicle card");
      }
    } catch (err) {
      console.error("Error updating vehicle card:", err);
      const errorMsg = err?.response?.data?.errors 
        ? (typeof err.response.data.errors === "object"
            ? Object.entries(err.response.data.errors).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`).join("; ")
            : String(err.response.data.errors))
        : err?.message || "Failed to update vehicle card";
      setUpdateError(errorMsg);
    }
  };

  const handleDeactivate = async (item) => {
    try {
      const res = await deactivateVehicleCard(item.id);
      if (res?.id) {
        setItems(items.map((x) => (x.id === res.id ? res : x)));
      }
    } catch (err) {
      console.error("Error deactivating vehicle card:", err);
    }
  };

  const activeItems = items.filter((item) => item.active);
  const cards = activeItems.filter((item) => item.card_type === "card");
  const services = activeItems.filter((item) => item.card_type === "service");

  return (
    <div className="space-y-6">
      {canManage && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t("Cards & Services")}</h3>
            <p className="text-sm text-gray-500">{t("Manage cards and services for this vehicle")}</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
          >
            {t("Add Card/Service")}
          </button>
        </div>
      )}

      {!canManage && (
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("Cards & Services")}</h3>
          <p className="text-sm text-gray-500">{t("Available cards and services for this vehicle")}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-gray-500">{t("Loading...")}</div>
      ) : activeItems.length === 0 ? (
        <div className="text-sm text-gray-500">
          {items.length === 0 
            ? t("No cards or services yet") 
            : t("No active cards or services")}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Cards Section */}
          {cards.length > 0 && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <h4 className="text-md font-medium text-gray-900">{t("Cards")}</h4>
                </div>
                <ul className="divide-y divide-gray-200">
                  {cards.map((card) => (
                    <li key={card.id} className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {card.issuer} {card.card_number && `- ${card.card_number}`}
                          </div>
                          {card.pin_code && (
                            <div className="text-sm text-gray-600">
                              {t("PIN")}: {card.pin_code}
                            </div>
                          )}
                          {card.description && (
                            <div className="text-sm text-gray-600 mt-1">{card.description}</div>
                          )}
                          {card.module_tags && card.module_tags.length > 0 && (
                            <div className="mt-2">
                              <TagsView tags={card.module_tags} />
                            </div>
                          )}
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => setEditItem(card)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              {t("Edit")}
                            </button>
                            <button
                              onClick={() => handleDeactivate(card)}
                              className="text-sm text-gray-700 hover:text-gray-900"
                            >
                              {t("Deactivate")}
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Services Section */}
          {services.length > 0 && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <h4 className="text-md font-medium text-gray-900">{t("Services")}</h4>
                </div>
                <ul className="divide-y divide-gray-200">
                  {services.map((service) => (
                    <li key={service.id} className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{service.service_name}</div>
                          {service.description && (
                            <div className="text-sm text-gray-600 mt-1">{service.description}</div>
                          )}
                          {service.module_tags && service.module_tags.length > 0 && (
                            <div className="mt-2">
                              <TagsView tags={service.module_tags} />
                            </div>
                          )}
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => setEditItem(service)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              {t("Edit")}
                            </button>
                            <button
                              onClick={() => handleDeactivate(service)}
                              className="text-sm text-gray-700 hover:text-gray-900"
                            >
                              {t("Deactivate")}
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateCardModal
          onClose={() => {
            setShowCreate(false);
            setCreateError(null);
          }}
          onSubmit={handleCreate}
          t={t}
          error={createError}
        />
      )}

      {/* Edit Modal */}
      {editItem && (
        <EditCardModal
          item={editItem}
          onClose={() => {
            setEditItem(null);
            setUpdateError(null);
          }}
          onSubmit={handleUpdate}
          t={t}
          error={updateError}
        />
      )}
    </div>
  );
};

export default VehicleCardsPanel;
